import { DropdownMenu, IconButton } from "@radix-ui/themes";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { FaChevronDown } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";

import { MarketingLink } from "@/components/marketing/MarketingButton";
import {
  LABELS_PATH,
  MARKETING_PRODUCTS,
  PACKAGE_PRODUCTS,
  PREPARE_PRODUCTS,
  type MarketingProductKey,
} from "@/components/marketing/products";
import { cn } from "@/lib/utils";

const itemClasses = "cursor-pointer rounded-[6px] font-semibold";
const labelClasses =
  "h-7 px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-text-secondary";

function linkClasses(isDark: boolean, active: boolean) {
  return cn(
    "flex items-center gap-1 whitespace-nowrap font-semibold text-[14px]",
    isDark
      ? "text-white/70 hover:text-white"
      : "text-[#525966] hover:text-brand-primary",
    active && (isDark ? "text-white" : "text-brand-primary"),
  );
}

function ProductItems({ keys }: { keys: MarketingProductKey[] }) {
  return keys.map((key) => (
    <DropdownMenu.Item key={key} asChild className={itemClasses}>
      <MarketingLink to={MARKETING_PRODUCTS[key].path}>
        {MARKETING_PRODUCTS[key].name}
      </MarketingLink>
    </DropdownMenu.Item>
  ));
}

function FeatureGroups() {
  return (
    <>
      <DropdownMenu.Label className={labelClasses}>Tools</DropdownMenu.Label>
      <ProductItems keys={PREPARE_PRODUCTS} />
      <DropdownMenu.Separator className="bg-border-tertiary" />
      <DropdownMenu.Label className={labelClasses}>
        Marketplace
      </DropdownMenu.Label>
      <ProductItems keys={PACKAGE_PRODUCTS} />
    </>
  );
}

const AUDIENCES = [
  { label: "For Artists", to: "/artists" },
  { label: "For Labels", to: LABELS_PATH },
  { label: "For Fans", to: "/" },
];

function AudienceItems({ current }: { current?: string }) {
  return AUDIENCES.map((audience) => (
    <DropdownMenu.Item
      key={audience.to}
      asChild
      className={cn(
        itemClasses,
        audience.to === current && "text-brand-primary",
      )}
    >
      <MarketingLink to={audience.to}>{audience.label}</MarketingLink>
    </DropdownMenu.Item>
  ));
}

/** Logged-out links to the marketing pages, desktop. */
export function MarketingNavLinks({ isDark }: { isDark: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onFeature = [...PREPARE_PRODUCTS, ...PACKAGE_PRODUCTS].some(
    (key) => pathname === MARKETING_PRODUCTS[key].path,
  );
  const audience = AUDIENCES.find((a) => a.to === pathname);

  return (
    <>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          <button
            type="button"
            className={cn(linkClasses(isDark, !!audience), "cursor-pointer")}
          >
            {(audience ?? AUDIENCES[0]).label}
            <FaChevronDown size={9} aria-hidden />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          variant="soft"
          color="gray"
          className="min-w-[180px] rounded-[10px]"
          align="start"
          sideOffset={10}
          collisionPadding={8}
        >
          <AudienceItems current={audience?.to} />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          <button
            type="button"
            className={cn(linkClasses(isDark, onFeature), "cursor-pointer")}
          >
            Features
            <FaChevronDown size={9} aria-hidden />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          variant="soft"
          color="gray"
          className="min-w-[230px] rounded-[10px]"
          align="start"
          sideOffset={10}
          collisionPadding={8}
        >
          <FeatureGroups />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </>
  );
}

/** Logged-out menu button for the marketing pages, below desktop width. */
export function MarketingNavMenu({ isDark }: { isDark: boolean }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          variant="ghost"
          size="3"
          aria-label="Open menu"
          className={cn(
            "cursor-pointer",
            isDark
              ? "text-white/70 hover:text-white"
              : "text-[#525966] hover:text-black",
          )}
        >
          <FiMenu size={22} />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        variant="soft"
        color="gray"
        className="max-h-[75vh] min-w-[240px] overflow-y-auto rounded-[10px]"
        align="end"
        sideOffset={8}
        collisionPadding={8}
      >
        <DropdownMenu.Item
          className={cn(itemClasses, "sm:hidden")}
          onSelect={() =>
            navigate({
              to: ".",
              search: (prev) => ({ ...prev, auth: "login" as const }),
            })
          }
        >
          Sign in
        </DropdownMenu.Item>
        <DropdownMenu.Separator className="bg-border-tertiary sm:hidden" />
        <AudienceItems />
        <DropdownMenu.Separator className="bg-border-tertiary" />
        <FeatureGroups />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
